import type { DestroyRef, WritableSignal } from '@angular/core';
import { inject, Injectable, Injector, runInInjectionContext, signal, untracked } from '@angular/core';
import { firstValueFrom, type Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { createTabForm, type TabForm } from '@sites/entity-signal-form';

import type { NormalizedTabEntity } from '@web/types';

import { TabsFacade } from '@web/store/page-structure/tabs/tabs.facade';
import { HistoryFacade } from '@web/store/history/history.facade';

type TabFormEntry = {
    form: TabForm;
    entitySignal: WritableSignal<NormalizedTabEntity>;
    storeSub: Subscription;
    refCount: number;
};

@Injectable({ providedIn: 'root' })
export class WebTabFormService {
    private readonly injector = inject(Injector);
    private readonly tabsFacade = inject(TabsFacade);
    private readonly historyFacade = inject(HistoryFacade);

    private readonly registry = new Map<string, TabFormEntry>();

    public getOrCreate(entityId: string): TabForm {
        const existing = this.registry.get(entityId);
        if (existing) {
            existing.refCount++;
            return existing.form;
        }

        const entitySignal = signal<NormalizedTabEntity>(undefined as any);

        // NgRx store.select() emits synchronously, so entitySignal is populated immediately
        const storeSub = this.tabsFacade
            .byId$(entityId)
            .pipe(filter(Boolean))
            .subscribe((value) => entitySignal.set(value));

        const form = runInInjectionContext(this.injector, () =>
            createTabForm({
                entity: entitySignal,
                onUpdate: (value) => {
                    const currentData = untracked(() => entitySignal()?.props);

                    this.historyFacade.addBatchUpdateToHistory(
                        'tab',
                        { id: value.id, changes: { props: currentData } },
                        { id: value.id, changes: { props: value.props } }
                    );
                    this.tabsFacade.updateProperties(value.id, {
                        props: value.props,
                        children: value.children as string[],
                    });
                },
                onRemove: async () => {
                    const entity = await firstValueFrom(this.tabsFacade.denormalizedById$(entityId));
                    if (!entity) {
                        console.warn(`Tab with id ${entityId} not found in store during form destruction.`);
                        return;
                    }

                    this.tabsFacade.remove(entity);
                },
            })
        );

        // Sync immediately with the store value to avoid null data before the effect fires
        const initialEntity = entitySignal();
        if (initialEntity) {
            form.syncFromEntity(structuredClone(initialEntity));
        }

        this.registry.set(entityId, { form, entitySignal, storeSub, refCount: 1 });
        return form;
    }

    public getOrCreateScoped(entityId: string, destroyRef: DestroyRef): TabForm {
        const form = this.getOrCreate(entityId);
        destroyRef.onDestroy(() => this.release(entityId));
        return form;
    }

    public getForm(tabId: string): TabForm | null {
        return this.registry.get(tabId)?.form ?? null;
    }

    public release(tabId: string): void {
        const entry = this.registry.get(tabId);
        if (!entry) {
            return;
        }

        entry.refCount--;
        if (entry.refCount <= 0) {
            this.deleteForm(tabId);
        }
    }

    public deleteForm(tabId: string): void {
        const entry = this.registry.get(tabId);
        if (!entry) {
            return;
        }

        entry.storeSub.unsubscribe();
        entry.form.destroy();
        this.registry.delete(tabId);
    }
}
